import { RefObject, useEffect, useMemo, useState } from 'react';
import { AREngine, getMindTargetUrl, getPreferredAREngine } from '../utils/arEngine';
import { ensureMindAR } from '../utils/mindArReady';
import { useXR8Scene } from './useXR8Scene';

export interface UseARSceneOptions {
    targetJsonUrl: string;
    sceneRef: RefObject<HTMLElement>;
}

export interface UseARSceneResult {
    engine: AREngine;
    ready: boolean;
    targetName: string | null;
    mindTargetUrl: string;
}

export const useARScene = ({
    targetJsonUrl,
    sceneRef,
}: UseARSceneOptions): UseARSceneResult => {
    const engine = useMemo(() => getPreferredAREngine(), []);
    const mindTargetUrl = useMemo(
        () => getMindTargetUrl(targetJsonUrl),
        [targetJsonUrl],
    );

    const [mindArReady, setMindArReady] = useState(engine !== 'mindar');

    const { ready, targetName } = useXR8Scene(
        targetJsonUrl,
        sceneRef,
        engine === 'xr8',
    );

    useEffect(() => {
        if (engine !== 'mindar') {
            return;
        }

        let cancelled = false;

        ensureMindAR()
            .then(() => {
                if (!cancelled) {
                    setMindArReady(true);
                }
            })
            .catch((error) => {
                console.error('MindAR failed to load', error);
            });

        return () => {
            cancelled = true;
        };
    }, [engine]);

    if (engine === 'mindar') {
        return {
            engine,
            ready: mindArReady,
            targetName: null,
            mindTargetUrl,
        };
    }

    return {
        engine,
        ready,
        targetName,
        mindTargetUrl,
    };
};
